/**
 * electric auth token create
 *
 * Create a new API token.
 */

import { Command } from 'commander'
import { createClient } from '../../../client.js'
import { resolveWorkspace } from '../../../lib/workspace-resolver.js'
import { handleError } from '../../../lib/errors.js'
import {
  isQuiet,
  printQuiet,
  printJson,
  printKeyValue,
  printSuccess,
} from '../../../output.js'

export const tokenCreateCommand = new Command(`create`)
  .usage(`--name <name> --scopes <scopes> [options]`)
  .description(`Create a new API token`)
  .requiredOption(`--name <name>`, `Token name`)
  .requiredOption(`--scopes <scopes>`, `Comma-separated scope list`)
  .option(`--workspace <id>`, `Workspace ID`)
  .option(`--project-ids <ids>`, `Comma-separated project ID list`)
  .option(`--expires-at <date>`, `Expiration date (ISO 8601)`)
  .addHelpText(
    `after`,
    `\nAvailable scopes:\n` +
      `  v2:projects:read          Read project details\n` +
      `  v2:projects:write         Create, update, and delete projects\n` +
      `  v2:environments:read      Read environment details\n` +
      `  v2:environments:write     Create, update, and delete environments\n` +
      `  v2:services:read          Read service details\n` +
      `  v2:services:write         Create, update, and delete services\n` +
      `  v2:services:secrets       Read service connection credentials\n`
  )
  .action(async (opts) => {
    const json = tokenCreateCommand.parent?.parent?.parent?.opts().json ?? false
    try {
      const client = createClient()
      const workspaceId = await resolveWorkspace(client, opts.workspace)
      const scopes = opts.scopes.split(`,`).map((s: string) => s.trim())
      const projectIds = opts.projectIds
        ? opts.projectIds.split(`,`).map((s: string) => s.trim())
        : undefined

      const result = await client.tokens.create({
        workspaceId,
        name: opts.name,
        scopes,
        projectIds,
        expiresAt: opts.expiresAt ?? undefined,
      })

      if (isQuiet()) {
        printQuiet(result.id)
        return
      }

      if (json) {
        printJson(result)
      } else {
        printSuccess(`Token created successfully.\n`)
        printKeyValue([
          [`ID:`, result.id],
          [`Token:`, result.token],
        ])
        process.stdout.write(
          `\nSave this token — it cannot be retrieved again.\n`
        )
      }
    } catch (error) {
      await handleError(error, json)
    }
  })
